import type { ReactNode } from "react";
import { ArrowLeft } from "lucide-react";
import { cn } from "../../lib/utils";

interface PageHeaderProps {
  /** Small uppercase eyebrow above the title (e.g. "Group", "Expense"). */
  eyebrow?: ReactNode;
  /** Page title. Omit for a header that's just a back link (e.g. when the
   *  page renders its own hero below). */
  title?: ReactNode;
  /** Optional supporting copy under the title. */
  description?: ReactNode;
  /** Optional action(s) rendered on the right (e.g. a primary button). */
  action?: ReactNode;
  /** Renders a back affordance above the title when provided. */
  back?: { label: ReactNode; onClick?: () => void };
  className?: string;
}

/**
 * Standard page header: an optional back link, eyebrow + title + description,
 * and an optional trailing action. Lives on the page background (not inside a
 * panel) so every screen opens with the same hierarchy and spacing.
 */
export function PageHeader({
  eyebrow,
  title,
  description,
  action,
  back,
  className,
}: PageHeaderProps): ReactNode {
  return (
    <div className={cn("space-y-4", className)}>
      {back ? (
        <button
          type="button"
          onClick={back.onClick}
          className="inline-flex cursor-pointer items-center gap-2 text-label-md text-on-surface-variant transition-colors hover:text-on-surface"
        >
          <ArrowLeft className="size-4" />
          {back.label}
        </button>
      ) : null}
      {title !== undefined || action !== undefined ? (
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="space-y-1">
            {eyebrow !== undefined ? (
              <p className="text-label-sm text-on-surface-variant">{eyebrow}</p>
            ) : null}
            {title !== undefined ? (
              <h1 className="text-headline-lg">{title}</h1>
            ) : null}
            {description !== undefined ? (
              <p className="max-w-2xl text-body-md text-on-surface-variant">
                {description}
              </p>
            ) : null}
          </div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
